<template>
  <drag-scroll-helper scroll-container-selector=".vue-recycle-scroller.scroller">
    <dynamic-scroller
      id="schedule-view"
      ref="scroller"
      :items="rows"
      :min-item-size="100"
      class="scroller"
    >
      <template #before>
        <div id="schedule-view-top-row">
          <week
            v-if="resourceTypeRow"
            first
            :week-days="weekDays"
            @drop="(date) => onDrop(date, resourceTypeRow)"
          >
            <template #controls>
              <resource-type-group
                :unscheduled-job-count="unassignedJobs.length"
                :units-overview="resourceTypeOverview"
                :name="resourceTypeName"
              />
            </template>
            <template #overview="{ day }">
              {{ resourceTypeRow.overview[day] }}
            </template>
            <template #events>
              <event-cell
                v-for="assignmentGroup in resourceTypeRow.jobs"
                :key="`assignment-group__${assignmentGroup[0].id}`"
                :week-start="weekDays[0].date"
                :events="assignmentGroup"
                v-on="$listeners"
              />
            </template>
          </week>
        </div>
        <section v-if="!rows.length" class="pt-0">
          <slot name="empty" />
        </section>
      </template>

      <template #default="{ item: row, index, active }">
        <dynamic-scroller-item :item="row" :active="active" :data-index="index">
          <week
            :last="index === rows.length - 1"
            :week-days="weekDays"
            @drop="(date) => onDrop(date, row)"
          >
            <template #controls>
              <company-group v-if="isServiceProvider(row.type)" :name="row.item.name" />
              <unit-group
                v-if="isResource(row.type)"
                :unit-number-id="row.id"
                unit-side-sheet-url="svwv-unit-overview"
              />
            </template>
            <template #overview="{ day }">
              {{ row.overview[day] }}
            </template>
            <template #events>
              <event-cell
                v-for="assignmentGroup in row.jobs"
                :key="`assignment-group__${assignmentGroup[0].id}`"
                :week-start="weekDays[0].date"
                :events="assignmentGroup"
                v-on="$listeners"
              />
            </template>
          </week>
        </dynamic-scroller-item>
      </template>
    </dynamic-scroller>
  </drag-scroll-helper>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import moment from 'moment'
import CompanyGroup from '@/components/weekly-schedule/CompanyGroup.vue'
import DragScrollHelper from '@/components/common/DragScrollHelper.vue'
import ResourceTypeGroup from '@/components/weekly-schedule/ResourceTypeGroup.vue'
import UnitGroup from '@/components/schedule/UnitGroup.vue'
import EventCell from '@/components/weekly-schedule/EventCell'
import Week from '@/components/weekly-schedule/Week.vue'
import { UnitNumber } from '@/models/unit_number'
import ResourceType from '@/models/resource_type'
import { groupBy, orderBy } from 'lodash'
import {
  constructResourceTypeOverview,
  getWeekOverviewForJobs,
  groupJobs,
  Item,
  Row,
  RowType,
  WeekDays,
} from '@/components/weekly-schedule/utils'
import { useStore, useGetters } from 'vuex-composition-helpers'
import { BusinessUnit } from '@/models/business-unit'
import Division from '../../models/division'
import CurrentUserContext from '@/models/current-user-context'

const store = useStore()
const emit = defineEmits(['drop:day'])
interface Props {
  resourceType: ResourceType
  jobs: Item[]
  weekDays: WeekDays
  filters: {
    selectedDivisionIds: (string | number)[]
    selectedCompanyIds: (string | number)[]
    showOfflineUnits: boolean
    showStandbyUnits: boolean
    showUnitsWithNoAssignedJobs: boolean
  }
}
const props = defineProps<Props>()

const { getById: getDivisionById } = useGetters('divisions', ['getById'])

const businessUnits = computed<BusinessUnit[]>(() => store.state.businessUnits.all)
const divisions = computed<Record<number, Division>>(() => store.state.divisions.all)
const resources = computed<Record<string, UnitNumber>>(() => store.state.unitNumbers.all)
const userContext = computed<CurrentUserContext>(() => store.state.permissions.context)

const unassignedJobs = computed(() =>
  props.jobs.filter(
    (j) => j.status === 'Requested' && userContext.value.lineage.some((l) => l.id === j.ownerId),
  ),
)

const lookup = computed(() => {
  const assignedJobs = props.jobs.filter((j) => j.status !== 'Requested')
  const lookup: Record<`unit-${number}` | `company-${number}`, Item[]> = {}
  for (const job of assignedJobs) {
    if (job.unitId) {
      if (!(`unit-${job.unitId}` in lookup)) {
        lookup[`unit-${job.unitId}`] = []
      }
      lookup[`unit-${job.unitId}`].push(job)
    } else {
      if (!(`company-${job.serviceProviderBusinessUnitId}` in lookup)) {
        lookup[`company-${job.serviceProviderBusinessUnitId}`] = []
      }
      lookup[`company-${job.serviceProviderBusinessUnitId}`].push(job)
    }
  }
  return lookup
})

const resourcesForSelectedResourceType = computed(() =>
  orderBy(
    Object.values(resources.value).filter(
      (r) => r.isVisible && r.resourceTypeId === props.resourceType.id,
    ),
    [
      (unit: UnitNumber) =>
        unit.organizationId ? businessUnits.value[unit.organizationId]?.name?.toLowerCase() : '',
      'isOffline',
      'isActive',
      'isStandby',
      (unit: UnitNumber) => unit.name?.toLowerCase(),
      (unit: UnitNumber) => (unit.divisionId ? divisions.value[unit.divisionId]?.name : null),
    ],
  ),
)

const filteredResources = computed(() => {
  const visibleUnits = resourcesForSelectedResourceType.value
  return visibleUnits.filter(
    (unit: UnitNumber) =>
      (props.filters.showOfflineUnits || !unit.isOffline) &&
      (props.filters.showStandbyUnits || !unit.isStandby) &&
      (props.filters.showUnitsWithNoAssignedJobs || props.jobs.some((j) => j.unitId === unit.id)) &&
      (props.filters.selectedDivisionIds.some(
        (id) => id === getDivisionById.value(unit.divisionId)?.uuid,
      ) ||
        (unit.divisionId === null &&
          store.getters['permissions/getRoleAssignmentBusinessUnitIds'].some(
            (b) => b === unit.ownerId,
          ))) &&
      props.filters.selectedCompanyIds.some((id) => id === unit.organizationId),
  )
})

const resourceTypeOverview = computed(() =>
  constructResourceTypeOverview(
    filteredResources.value,
    props.filters.showStandbyUnits,
    props.filters.showOfflineUnits,
    resourcesForSelectedResourceType.value.length,
  ),
)

const isTwoTier = computed(() => {
  const partners = new Set(userContext.value.lineage.flatMap((l) => l.partnerIds))
  return partners.size > 0
})

const resourceTypeRow = computed<Row | null>(() => {
  if (isTwoTier.value) {
    return {
      id: props.resourceType.id ?? -1,
      type: RowType.ResourceType,
      item: props.resourceType,
      jobs: groupJobs(
        unassignedJobs.value.filter(
          (j) =>
            props.filters?.selectedDivisionIds.includes(j.division.id) ||
            props.filters?.selectedDivisionIds.includes(getDivisionById.value(j.division.id)?.uuid),
        ),
      ),
      overview: getWeekOverviewForJobs(unassignedJobs.value, props.weekDays[0].date, 'unassigned'),
    }
  }

  const [serviceProvider] = userContext.value.lineage
    .filter((l) => l.type === 'Organization')
    .map((l) => businessUnits.value[l.id])
  const jobs = props.jobs.filter(
    (j) =>
      j.unitId === null &&
      userContext.value.lineage.some((l) => l.id === j.serviceProviderBusinessUnitId) &&
      !j.isCancelled,
  )

  if (!serviceProvider) {
    return null
  }

  return {
    id: serviceProvider.id,
    type: RowType.ServiceProvider,
    item: serviceProvider,
    jobs: groupJobs(jobs),
    overview: getWeekOverviewForJobs(jobs, props.weekDays[0].date),
  }
})

const rows = computed(() => {
  const bySp = groupBy(filteredResources.value, 'organizationId')
  return Object.entries(bySp).flatMap(([organizationId, unitNumbers]) => {
    const rows: Row[] = []
    if (isTwoTier.value) {
      const serviceProvider = businessUnits.value[organizationId]
      if (!serviceProvider) {
        return []
      }
      const serviceProviderJobs = lookup.value[`company-${organizationId}`]?.filter(
        (j) => !j.isCancelled,
      )

      rows.push({
        id: organizationId,
        type: RowType.ServiceProvider,
        item: serviceProvider,
        jobs: groupJobs(serviceProviderJobs),
        overview: getWeekOverviewForJobs(
          lookup.value[`company-${organizationId}`],
          props.weekDays[0].date,
        ),
      })
    }
    rows.push(
      ...[
        ...unitNumbers.map((un: UnitNumber) => ({
          id: un.id || -1,
          type: RowType.Resource,
          item: un,
          jobs: groupJobs(lookup.value[`unit-${un.id}`]),
          overview: getWeekOverviewForJobs(lookup.value[`unit-${un.id}`], props.weekDays[0].date),
        })),
      ],
    )
    return rows
  })
})

const resourceTypeName = computed(() => {
  return props.resourceType.name ?? ''
})

function isServiceProvider(type: RowType) {
  return type === RowType.ServiceProvider
}

function isResource(type: RowType) {
  return type === RowType.Resource
}

function onDrop(date: moment.Moment, row: Row | null) {
  emit('drop:day', { date, type: row?.type, entity: row?.item })
}
</script>

<style scoped>
section {
  contain: content;
  padding: var(--space-base);
}

.scroller {
  height: 100%;
}

:deep(.vue-recycle-scroller__slot) {
  position: sticky;
  top: 0;
  z-index: 1;
  margin-top: var(--space-base);
  background: var(--color-silver--light);
}

:deep(.controls) {
  background-color: var(--color-silver--light);
  border-right: 1px solid var(--color-grey--lighter);
}

.cell :deep(.details) {
  flex: 1;
  min-width: 0;
  width: 100%;
  padding-right: var(--space-smallest);
}

.cell :deep(.details__location) {
  font-weight: bolder;
}

.cell :deep(.icon) {
  flex: 0;
}

.cell :deep(.content) {
  color: var(--color-black);
  font-size: 12px;
  line-height: 1.5;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.new-priority) {
  border-top: 1px solid var(--color-blue);
  border-bottom: 1px solid var(--color-blue);
  border-right: 1px solid var(--color-blue);
}

:deep(.cell:hover) {
  background-color: #eee;
}

:deep(.cell.selected) {
  outline: 2px solid var(--color-yellow--dark);
}

:deep(.cell.selected:hover) {
  background-color: var(--color-blue--lighter);
}

.cell.expanded {
  height: auto;
  flex-direction: column;
}

.cell :deep(.expanded-details) {
  position: relative;
  display: grid;
  grid-auto-flow: column;
  min-width: 0;
  width: 100%;
  padding-right: var(--space-smallest);
  padding-left: 0.7em;
}

.cell.drop-target {
  background-color: #00acc1;
  z-index: 10;
}
</style>
